import {ChangeDetectionStrategy, Component, computed, input} from '@angular/core';
import * as dayjs from 'dayjs';
import {EnumsService} from 'src/app/services/enums.service';
import {ToolsService} from 'src/app/services/tools.service';
import {WeekDayIndexesEnum, WeekDaysEnum} from 'src/app/enums/CalendarEnums';
import {OpenHour} from 'src/app/types/OpenHour';

@Component({
  selector: 'app-openning-hours-small-v2',
  templateUrl: './openning-hours-small-v2.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OpenningHoursSmallV2Component {
  public openHours = input<OpenHour[]>(null);
  public openningHoursString = computed<string>(() => this.updateOpenningHours(this.openHours()));

  constructor(
    private tools: ToolsService,
    private enumsService: EnumsService,
  ) {
    dayjs.locale('sv');
  }

  private updateOpenningHours(openHours: OpenHour[]): string {
    let openningHoursString: string = '';
    if (!this.tools.isNil(openHours)) {
      const currentDayNumber: number = dayjs().day();
      const currentHour: string = dayjs().format('HH:mm:ss');

      const openHourNow: OpenHour = openHours.find(
        (item) =>
          item.fromDay <= currentDayNumber &&
          item.toDay >= currentDayNumber &&
          item.fromTime < currentHour &&
          item.toTime > currentHour &&
          item.isCommentOnly !== true,
      );

      if (!this.tools.isNil(openHourNow)) {
        openningHoursString = this.generateConditionalOpeningHoursString(openHourNow, currentHour, currentDayNumber);
      }

      const openHour: OpenHour = this.openHours().find(
        (item) => item.fromDay <= currentDayNumber && item.toDay >= currentDayNumber && item.isCommentOnly !== true,
      );

      if (!this.tools.isNil(openHour) && this.tools.isNil(openHourNow)) {
        openningHoursString = this.generateConditionalOpeningHoursString(openHour, currentHour, currentDayNumber);
      }

      const openHourFuture: OpenHour = this.openHours().find((item) => item.isCommentOnly !== true);
      if (this.tools.isNil(openHour) && this.tools.isNil(openHourNow) && !this.tools.isNil(openHourFuture)) {
        openningHoursString = this.getFutureOpeningHoursString(openHourFuture);
      }
    }
    return openningHoursString;
  }

  private generateConditionalOpeningHoursString(
    openHour: OpenHour,
    currentHour: string,
    currentDayNumber: number,
  ): string {
    if (openHour.fromTime < currentHour && openHour.toTime > currentHour) {
      return this.getTodayOpeningHoursString(openHour);
    } else if (openHour.toTime > currentHour) {
      return this.getTodayLaterOpeningHoursString(openHour);
    } else {
      return this.getTomorrowOpeningHoursString(currentDayNumber);
    }
  }

  private getTodayOpeningHoursString(openHour: OpenHour): string {
    return `Öppet idag till ${this.formatHoursToDisplay(openHour.toTime)}`;
  }

  private getTodayLaterOpeningHoursString(openHour: OpenHour): string {
    return `Öppet idag från ${this.formatHoursToDisplay(openHour.fromTime)}`;
  }

  private getFutureOpeningHoursString(openHour: OpenHour): string {
    const key: string = this.enumsService.getEnumKeyByEnumValue(WeekDayIndexesEnum, openHour.fromDay);
    const weekDay: string =
      !this.tools.isNil(WeekDaysEnum) && !this.tools.isNil(WeekDaysEnum[key]) ? WeekDaysEnum[key].toLowerCase() : '';
    return `Öppet ${weekDay} från ${this.formatHoursToDisplay(openHour.fromTime)}`;
  }

  private getTomorrowOpeningHoursString(currentDayNumber: number): string {
    let openingHoursString = '';
    const nextDayNumber = currentDayNumber === 7 ? 1 : currentDayNumber + 1;
    const openHourNext: OpenHour = this.openHours().find(
      (item) => item.fromDay <= nextDayNumber && item.toDay >= nextDayNumber && item.isCommentOnly !== true,
    );
    if (!this.tools.isNil(openHourNext)) {
      openingHoursString = `Öppet i morgon från ${this.formatHoursToDisplay(openHourNext.fromTime)}`;
    }
    return openingHoursString;
  }

  private formatHoursToDisplay(hour: string): string {
    return hour.substring(0, 5);
  }
}
